// =============================================================================
// src/features/analytics/analytics-feed-page-store.ts — Pagina viva do feed.
//
// 2026-09-20. Estado da lista paginada de mencoes: linhas da pagina, total do
// recorte, se existe proxima e se ha busca em voo. Store separada da
// analytics-live-store de proposito — o ciclo de vida e outro: aquela guarda o
// dashboard memoizado por recorte, esta troca a cada clique em "proxima",
// "ordenar" ou num chip de canal.
//
// LATEST-WINS, e nao trava. O drill-down antigo usava uma flag `actorFeedLoading`
// que DESCARTAVA cliques enquanto havia fetch em voo; com paginacao isso e
// inaceitavel: dois cliques rapidos em "proxima" e a pagina exibida deixa de
// bater com o numero no campo. Aqui cada pedido leva um numero de sequencia e a
// resposta atrasada e ignorada — nenhum clique se perde, nenhuma corrida.
// =============================================================================

import type { OmarAnalyticsMention } from './analytics-types';

/** O que o pedido de pagina carrega — o recorte ja resolvido pelo controller. */
export interface AnalyticsFeedPageLoad {
  readonly page: number;
  readonly rawPlatforms: readonly string[];
  readonly actorSlugs: readonly string[];
  readonly relations: readonly string[];
  readonly relationPairs: readonly string[];
  /** false quando so a pagina mudou: o total nao muda e nao precisa ser refeito. */
  readonly refreshTotal: boolean;
}

export interface AnalyticsFeedPageResult {
  readonly mentions: readonly OmarAnalyticsMention[];
  readonly hasNextPage: boolean;
  /** null = a contagem nao respondeu; a nav degrada para anterior/proxima. */
  readonly totalItems: number | null;
}

export interface AnalyticsFeedPageState extends AnalyticsFeedPageResult {
  readonly page: number;
  readonly isLoading: boolean;
}

export type AnalyticsFeedPageOutcome = 'applied' | 'unavailable' | 'failed';

type AnalyticsFeedPageLoader = (pageLoad: AnalyticsFeedPageLoad) => Promise<AnalyticsFeedPageResult>;

const ANALYTICS_EMPTY_FEED_PAGE: AnalyticsFeedPageState = {
  mentions: [], hasNextPage: false, totalItems: null, page: 1, isLoading: false,
};

let analyticsFeedPageState: AnalyticsFeedPageState = ANALYTICS_EMPTY_FEED_PAGE;
let analyticsFeedPageLoader: AnalyticsFeedPageLoader | null = null;
let analyticsFeedPageSeq = 0;

/** Registrado pelo composition root quando o Analytics live esta habilitado. */
export function setAnalyticsFeedPageLoader(loader: AnalyticsFeedPageLoader | null): void {
  analyticsFeedPageLoader = loader;
}

/** true quando a paginacao pelo servidor existe (live). */
export function analyticsFeedPageAvailable(): boolean {
  return analyticsFeedPageLoader !== null;
}

/** Pagina corrente para o render (markup do feed + nav). */
export function getAnalyticsFeedPageState(): AnalyticsFeedPageState {
  return analyticsFeedPageState;
}

/**
 * Busca uma pagina e publica o resultado. Resposta de pedido VELHO e descartada.
 *
 * O total so e refeito quando o recorte muda (`refreshTotal`): ele custa um scan
 * de ~80 ms no servidor e nao muda entre paginas do mesmo conjunto.
 *
 * @example await requestAnalyticsFeedPage({ page: 3, rawPlatforms: [], ..., refreshTotal: false })
 */
export async function requestAnalyticsFeedPage(
  pageLoad: AnalyticsFeedPageLoad,
): Promise<AnalyticsFeedPageOutcome> {
  if (!analyticsFeedPageLoader) return 'unavailable';
  const seq = ++analyticsFeedPageSeq;
  analyticsFeedPageState = { ...analyticsFeedPageState, isLoading: true };
  try {
    const result = await analyticsFeedPageLoader(pageLoad);
    if (seq !== analyticsFeedPageSeq) return 'applied'; // resposta atrasada: ignora
    analyticsFeedPageState = {
      ...result,
      // O total SOBREVIVE a troca de pagina. Ele so e rebuscado quando o recorte
      // muda (`refreshTotal`), e sem esta guarda a pagina 2 apagaria "de 858" —
      // a nav degradava para "Pagina 2" como se a contagem tivesse falhado.
      totalItems: pageLoad.refreshTotal ? result.totalItems : analyticsFeedPageState.totalItems,
      page: pageLoad.page,
      isLoading: false,
    };
    return 'applied';
  } catch {
    if (seq === analyticsFeedPageSeq) analyticsFeedPageState = { ...analyticsFeedPageState, isLoading: false };
    return 'failed';
  }
}

export function __resetAnalyticsFeedPageForTests(): void {
  analyticsFeedPageState = ANALYTICS_EMPTY_FEED_PAGE;
  analyticsFeedPageLoader = null;
  analyticsFeedPageSeq = 0;
}
